/**
 * Notification Service
 *
 * API client for notification endpoints.
 */

import { getAuthToken, getApiBaseUrl } from './api.js';

const API_BASE = `${getApiBaseUrl()}/v1`;

/**
 * Get authorization headers
 */
function getAuthHeaders() {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

/**
 * Fetch notifications for the current user
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Items per page (default: 20)
 * @param {boolean} params.unreadOnly - Only fetch unread (default: false)
 * @returns {Promise<{notifications: Array, total: number, unreadCount: number}>}
 */
export async function getNotifications({ page = 1, pageSize = 20, unreadOnly = false } = {}) {
  // Don't make API call if not authenticated
  const token = getAuthToken();
  if (!token) {
    return { notifications: [], total: 0, unreadCount: 0 };
  }

  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  if (unreadOnly) {
    params.append('unreadOnly', 'true');
  }

  try {
    const response = await fetch(`${API_BASE}/notifications?${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch notifications: ${response.status}`);
    }

    return response.json();
  } catch {
    // Network error - return empty list to avoid breaking UI
    return { notifications: [], total: 0, unreadCount: 0 };
  }
}

/**
 * Get unread notification count
 * @returns {Promise<{count: number, hasCritical: boolean}>}
 */
export async function getUnreadCount() {
  // Don't make API call if not authenticated
  const token = getAuthToken();
  if (!token) {
    return { count: 0, hasCritical: false };
  }

  try {
    const response = await fetch(`${API_BASE}/notifications/unread/count`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      // Return 0 on error to avoid breaking the UI
      return { count: 0, hasCritical: false };
    }

    return response.json();
  } catch {
    // Network error (server unreachable) - return default to avoid breaking UI
    return { count: 0, hasCritical: false };
  }
}

/**
 * Mark a notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<{success: boolean, notification: Object}>}
 */
export async function markAsRead(notificationId) {
  const response = await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to mark notification as read: ${response.status}`);
  }

  return response.json();
}

/**
 * Mark all notifications as read
 * @returns {Promise<{success: boolean, updated_count: number}>}
 */
export async function markAllAsRead() {
  const response = await fetch(`${API_BASE}/notifications/mark-all-read`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to mark all as read: ${response.status}`);
  }

  return response.json();
}

/**
 * Delete a notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<{success: boolean}>}
 */
export async function deleteNotification(notificationId) {
  const response = await fetch(`${API_BASE}/notifications/${notificationId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete notification: ${response.status}`);
  }

  return response.json();
}

/**
 * Format relative time for notifications
 * @param {string} dateString - ISO date string
 * @returns {string} - Relative time string
 */
export function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/**
 * Get icon for notification type
 * @param {string} notificationType - Type of notification
 * @returns {string} - SVG icon HTML
 */
export function getNotificationIcon(notificationType) {
  const icons = {
    renewal_reminder: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>`,
    discrepancy_alert: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>`,
    invoice_processed: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>`,
    optimization_opportunity: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>`,
    system_alert: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>`,
  };

  return icons[notificationType] || icons.system_alert;
}

/**
 * Get color class for notification priority
 * @param {string} priority - Notification priority
 * @returns {string} - CSS class name
 */
export function getPriorityClass(priority) {
  const classes = {
    critical: 'notification-item--critical',
    high: 'notification-item--high',
    medium: 'notification-item--medium',
    low: 'notification-item--low',
  };

  return classes[priority] || '';
}
